import React, { useEffect, useRef, useState } from 'react';
import {
  Box, IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { getClassAttendanceStats } from '../../api/autoAttendance';
import { errorMessages } from '../../utils/methods';

// 목록의 출결 상태 배지와 같은 색
const STATUS = {
  attended: { label: '출석', color: '#0F7F85' },
  absent: { label: '결석', color: '#F44336' },
  unconfirmed: { label: '미확인', color: '#E65100' },
};
const PREVIOUS_COLOR = '#C9CED6';
const LEGEND_ROW_HEIGHT = 28;
const LEGEND_VISIBLE_ROWS = 5;
const DONUT_COLORS = ['#0F7F85', '#4AB3BC', '#1976D2', '#F2994A', '#9B51E0', '#EB5757', '#27AE60', '#828282'];

const percent = (part, total) => (total ? Math.round((part / total) * 1000) / 10 : 0);

// 애니메이션. 움직임 줄이기 설정이면 바로 최종값을 보여 준다
const ANIMATION_MS = 900;
const reduceMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const easeOutCubic = t => 1 - (1 - t) ** 3;

// 0 → target 으로 올라가는 숫자(소수 1자리까지)
function useCountUp(target, decimals = 0) {
  const [value, setValue] = useState(reduceMotion() ? target : 0);
  useEffect(() => {
    if (reduceMotion()) {
      setValue(target);
      return undefined;
    }
    let frame;
    const start = performance.now();
    const tick = now => {
      const t = Math.min((now - start) / ANIMATION_MS, 1);
      const factor = 10 ** decimals;
      setValue(Math.round(target * easeOutCubic(t) * factor) / factor);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, decimals]);
  return value;
}

// 처음 그릴 때 한 박자 뒤 true — 폭·길이를 0 에서 최종값으로 transition 시키는 데 쓴다
function useAppeared() {
  const [appeared, setAppeared] = useState(reduceMotion());
  useEffect(() => {
    const frame = requestAnimationFrame(() => setAppeared(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return appeared;
}

const drawLine = keyframes`
  from { stroke-dashoffset: 1; }
  to { stroke-dashoffset: 0; }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// 전월 대비 배지. good = 늘면 좋은 지표(출석·총 세션)인지
function DiffBadge({ diff, good }) {
  if (diff == null) return null;
  const flat = diff === 0;
  const better = good ? diff > 0 : diff < 0;
  const color = flat ? '#888' : better ? '#1E7E78' : '#C7362A';
  const background = flat ? '#F2F2F2' : better ? '#E3F4F2' : '#FDECEA';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '2px', px: '8px', py: '2px', borderRadius: '10px',
        fontSize: '12px', fontWeight: 700, color, background,
      }}>
      {flat ? '−' : diff > 0 ? '▲' : '▼'} {Math.abs(diff).toLocaleString()}건
    </Box>
  );
}

// center = 남는 세로 공간에서 내용을 가운데에(옆 카드와 높이가 같아 아래가 비지 않게)
export function Card({ title, children, sx, center }) {
  return (
    <Box
      sx={{
        background: '#fff', border: '1px solid #E8E8E8', borderRadius: '12px', p: '20px 24px',
        display: 'flex', flexDirection: 'column', ...sx,
      }}>
      {title && <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#262626', mb: 2 }}>{title}</Typography>}
      {center ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ width: '100%' }}>{children}</Box>
        </Box>
      ) : children}
    </Box>
  );
}

export function Empty() {
  return (
    <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography sx={{ fontSize: '14px', color: '#999' }}>조회된 데이터가 없습니다.</Typography>
    </Box>
  );
}

export function SummaryCard({ label, color, value, total, previous, previousTotal, good }) {
  const rate = total == null ? null : percent(value, total);
  const shownValue = useCountUp(value);
  const shownRate = useCountUp(rate ?? 0, 1);
  const appeared = useAppeared();
  const diff = previous == null ? null : value - previous;
  // 비율 지표는 퍼센트포인트 차이도 함께
  const previousRate = previous == null || previousTotal == null ? null : percent(previous, previousTotal);
  const rateDiff = rate == null || previousRate == null ? null : Math.round((rate - previousRate) * 10) / 10;
  return (
    <Box sx={{ flex: '1 1 200px', background: '#fff', border: '1px solid #E8E8E8', borderRadius: '12px', p: '20px 24px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>{label}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mt: '12px' }}>
        <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#262626', fontVariantNumeric: 'tabular-nums' }}>
          {shownValue.toLocaleString()}
          <Box component="span" sx={{ fontSize: '16px', ml: '2px' }}>건</Box>
        </Typography>
        {rate != null && (
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
            {shownRate.toFixed(1)}%
          </Typography>
        )}
      </Box>
      {diff != null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '8px', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '12px', color: '#888' }}>전월 대비</Typography>
          <DiffBadge diff={diff} good={good} />
          {rateDiff != null && (
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>
              비율 {rateDiff > 0 ? '+' : ''}
              {rateDiff}%p
            </Typography>
          )}
        </Box>
      )}
      {rate != null && (
        <Box sx={{ mt: '12px', height: 6, borderRadius: 3, background: '#F0F0F0', overflow: 'hidden' }}>
          <Box
            sx={{
              width: appeared ? `${rate}%` : 0, height: '100%', background: color,
              transition: `width ${ANIMATION_MS}ms cubic-bezier(0.23, 1, 0.32, 1)`,
            }}
          />
        </Box>
      )}
    </Box>
  );
}

// 일별 선 그래프: 이번 달(색)과 전월(회색)을 같은 일자 축에 겹친다
export function DailyLineChart({ current, previous, color, currentLabel, previousLabel }) {
  const W = 760;
  const H = 240;
  const pad = { left: 36, right: 16, top: 20, bottom: 28 };
  const days = Math.max(current.length, previous.length, 1);
  const max = Math.max(1, ...current, ...previous);
  const niceMax = Math.ceil(max / 4) * 4 || 4;
  const x = i => pad.left + ((W - pad.left - pad.right) * i) / Math.max(days - 1, 1);
  const y = v => pad.top + (H - pad.top - pad.bottom) * (1 - v / niceMax);
  const path = values => values.map((v, i) => `${i ? 'L' : 'M'}${x(i)},${y(v)}`).join(' ');
  const ticks = [0, 1, 2, 3, 4].map(k => (niceMax * k) / 4);

  if (!current.some(Boolean) && !previous.some(Boolean)) return <Empty />;
  return (
    <Box>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="일별 추이">
        {ticks.map(t => (
          <g key={t}>
            <line x1={pad.left} x2={W - pad.right} y1={y(t)} y2={y(t)} stroke="#EEE" strokeDasharray="4 4" />
            <text x={pad.left - 8} y={y(t) + 4} fontSize="11" fill="#999" textAnchor="end">{t}</text>
          </g>
        ))}
        {Array.from({ length: days }, (_, i) => i)
          .filter(i => i % 2 === 0)
          .map(i => (
            <text key={i} x={x(i)} y={H - 8} fontSize="11" fill="#999" textAnchor="middle">{i + 1}일</text>
          ))}
        {/* keyframes 는 sx 로 넘겨야 스타일시트에 등록된다(인라인 style 은 이름만 남아 동작하지 않음) */}
        <Box
          component="path"
          d={path(previous)} fill="none" stroke={PREVIOUS_COLOR} strokeWidth="2" pathLength="1"
          strokeDasharray="1" sx={{ animation: reduceMotion() ? 'none' : `${drawLine} ${ANIMATION_MS}ms ease-out both` }}
        />
        <Box
          component="path"
          d={path(current)} fill="none" stroke={color} strokeWidth="2.5" pathLength="1"
          strokeDasharray="1" sx={{ animation: reduceMotion() ? 'none' : `${drawLine} ${ANIMATION_MS * 1.3}ms ease-out both` }}
        />
        {/* 이번 달은 오늘까지만 그린다(앞으로의 날짜를 0으로 그리지 않게) */}
        {current.map((v, i) => (
          <Box
            component="g"
            key={i}
            sx={{
              animation: reduceMotion() ? 'none'
                : `${fadeIn} 300ms ease-out ${Math.round((ANIMATION_MS * 1.3 * i) / Math.max(current.length, 1))}ms both`,
            }}>
            <circle cx={x(i)} cy={y(v)} r="3" fill={color}>
              <title>{`${i + 1}일 ${v}건`}</title>
            </circle>
            {v > 0 && (
              <text x={x(i)} y={y(v) - 8} fontSize="10" fill="#555" textAnchor="middle">{v}</text>
            )}
          </Box>
        ))}
      </svg>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: '16px', mt: 1 }}>
        {[[previousLabel, PREVIOUS_COLOR], [currentLabel, color]].map(([label, c]) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
            <Typography sx={{ fontSize: '12px', color: '#555' }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// 도넛 + 건수 목록. item.color 가 있으면 그 색(상담 결과 분포 등), 없으면 순서대로
export function DonutChart({ items }) {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  const shownTotal = useCountUp(total);
  if (!total) return <Empty />;
  const R = 70;
  const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
      {/* 강사가 많으면 5줄 높이 안에서 얇은 스크롤로 넘겨 본다(브로제이 일정통계와 같은 방식) */}
      <Box
        sx={{
          flex: '1 1 140px', maxHeight: LEGEND_ROW_HEIGHT * LEGEND_VISIBLE_ROWS, overflowY: 'auto', pr: '10px',
          overscrollBehavior: 'contain', scrollbarWidth: 'thin', scrollbarColor: '#C4C9D0 transparent',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { background: '#C4C9D0', borderRadius: '2px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
        }}>
        {items.map((item, i) => (
          <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: LEGEND_ROW_HEIGHT }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: item.color || DONUT_COLORS[i % DONUT_COLORS.length] }} />
            <Typography sx={{ flex: 1, fontSize: '13px', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.name}
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#262626' }}>{item.value}건</Typography>
          </Box>
        ))}
      </Box>
      <svg viewBox="0 0 180 180" width="160" height="160" role="img" aria-label="강사별 비율">
        <g transform="rotate(-90 90 90)">
          {items.map((item, i) => {
            const length = (item.value / total) * C;
            const circle = (
              <Box
                component="circle"
                key={item.key}
                cx="90" cy="90" r={R} fill="none"
                stroke={item.color || DONUT_COLORS[i % DONUT_COLORS.length]} strokeWidth="22"
                strokeDasharray={`${length} ${C - length}`} strokeDashoffset={-offset}
                sx={{
                  animation: reduceMotion() ? 'none'
                    : `${keyframes`from { stroke-dasharray: 0 ${C}; } to { stroke-dasharray: ${length} ${C - length}; }`} ${
                      Math.max(120, (ANIMATION_MS * item.value) / total)}ms ease-out ${Math.round((ANIMATION_MS * offset) / C)}ms both`,
                }}>
                <title>{`${item.name} ${item.value}건 (${percent(item.value, total)}%)`}</title>
              </Box>
            );
            offset += length;
            return circle;
          })}
        </g>
        <text x="90" y="86" textAnchor="middle" fontSize="12" fill="#888">합계</text>
        <text x="90" y="106" textAnchor="middle" fontSize="18" fontWeight="700" fill="#262626">{shownTotal}</text>
      </svg>
    </Box>
  );
}

const headSx = { fontWeight: 600, background: '#F7F9FA', whiteSpace: 'nowrap' };

// 강사×수업유형 표 한 줄의 수업 유형~미확인률 칸. 비율은 막대와 함께 보여 준다
function RateBar({ value, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
      <Box sx={{ width: 64, height: 6, borderRadius: 3, background: '#EEE', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.min(value, 100)}%`, height: '100%', background: color }} />
      </Box>
      <Box component="span" sx={{ minWidth: 44, textAlign: 'right', fontWeight: 600, color }}>{value}%</Box>
    </Box>
  );
}

function RateCells({ group }) {
  const c = group.counts;
  const weight = group.subtotal ? 700 : 400;
  return (
    <>
      <TableCell align="center" sx={{ fontWeight: weight }}>{group.name}</TableCell>
      <TableCell align="center" sx={{ fontWeight: weight }}>{c.total}</TableCell>
      <TableCell align="center" sx={{ fontWeight: weight }}>{c.attended}</TableCell>
      <TableCell align="center" sx={{ fontWeight: weight }}>{c.absent}</TableCell>
      <TableCell align="center" sx={{ fontWeight: weight }}>{c.unconfirmed}</TableCell>
      <TableCell align="center"><RateBar value={percent(c.attended, c.total)} color={STATUS.attended.color} /></TableCell>
      <TableCell align="center"><RateBar value={percent(c.unconfirmed, c.total)} color={STATUS.unconfirmed.color} /></TableCell>
    </>
  );
}

// 강사별 수업유형 출석률 표. 지점 전체를 맨 위에 두고, 강사는 스크롤하면 TRAINER_PAGE 명씩 더 그린다(헤더 고정).
const TRAINER_PAGE = 10;
const stickyHeadSx = { ...headSx, position: 'sticky', top: 0, zIndex: 2 };
const groupCellSx = { fontWeight: 600, borderRight: '1px solid #F0F0F0', verticalAlign: 'middle', background: '#fff' };

function TrainerClassTypeTable({ data, summary }) {
  const [trainerKey, setTrainerKey] = useState('');
  const [visibleCount, setVisibleCount] = useState(TRAINER_PAGE);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  const trainers = trainerKey
    ? data.trainerClassTypes.filter(t => t.key === trainerKey)
    : data.trainerClassTypes;
  const visible = trainers.slice(0, visibleCount);
  const hasMore = visible.length < trainers.length;

  useEffect(() => {
    setVisibleCount(TRAINER_PAGE);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [trainerKey, data]);

  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) setVisibleCount(count => count + TRAINER_PAGE);
      },
      { root: scrollRef.current, rootMargin: '120px' },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, visibleCount]);

  return (
    <Card>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mb: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#262626' }}>강사별 수업유형 출석률</Typography>
        <TextField
          select size="small" label="강사" sx={{ minWidth: 160 }}
          value={trainerKey} onChange={e => setTrainerKey(e.target.value)}>
          <MenuItem value="">전체</MenuItem>
          {data.trainerClassTypes.map(t => (
            <MenuItem key={t.key} value={t.key}>{t.name}</MenuItem>
          ))}
        </TextField>
      </Box>
      {data.trainerClassTypes.length === 0 ? <Empty /> : (
        <Box ref={scrollRef} sx={{ overflow: 'auto', maxHeight: 560 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['강사', '수업 유형', '전체', '출석', '결석', '미확인', '출석률', '미확인률'].map(h => (
                  <TableCell key={h} align="center" sx={stickyHeadSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.classTypes.map((g, i) => (
                <TableRow key={`all-${g.key}`} sx={{ background: '#F7F9FA' }}>
                  {i === 0 && (
                    <TableCell
                      align="center" rowSpan={data.classTypes.length + 1}
                      sx={{ ...groupCellSx, fontWeight: 700, background: '#F7F9FA' }}>
                      지점 전체
                    </TableCell>
                  )}
                  <RateCells group={g} />
                </TableRow>
              ))}
              {data.classTypes.length > 0 && (
                <TableRow sx={{ background: '#F7F9FA', '& td': { borderBottom: '2px solid #E0E0E0' } }}>
                  <RateCells group={{ name: '합계', counts: summary, subtotal: true }} />
                </TableRow>
              )}
              {visible.map(t => {
                // 유형이 둘 이상이면 강사 소계 줄을 붙인다
                const rows = t.classTypes.length > 1
                  ? [...t.classTypes, { key: '__total', name: '소계', counts: t.total, subtotal: true }]
                  : t.classTypes;
                return rows.map((g, i) => (
                  <TableRow key={`${t.key}-${g.key}`} sx={g.subtotal ? { background: '#FAFAFA' } : undefined}>
                    {i === 0 && (
                      <TableCell align="center" rowSpan={rows.length} sx={groupCellSx}>{t.name}</TableCell>
                    )}
                    <RateCells group={g} />
                  </TableRow>
                ));
              })}
            </TableBody>
          </Table>
          {hasMore && <Box ref={sentinelRef} sx={{ height: 1 }} />}
        </Box>
      )}
    </Card>
  );
}

function ClassAttendanceStats({ branchId }) {
  const user = useSelector(state => state.user.user);
  const [month, setMonth] = useState(() => dayjs().startOf('month'));
  const monthKey = month.format('YYYY-MM');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['classAttendanceStats', branchId, monthKey],
    queryFn: () => getClassAttendanceStats(branchId, monthKey, user.token),
    enabled: !!branchId && !!user?.token,
  });

  const summary = data?.summary || { total: 0, attended: 0, absent: 0, unconfirmed: 0 };
  const previous = data?.previousSummary;
  const currentLabel = `${month.month() + 1}월`;
  const previousLabel = `${month.subtract(1, 'month').month() + 1}월`;
  const isCurrentMonth = month.isSame(dayjs(), 'month');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
        <IconButton size="small" onClick={() => setMonth(m => m.subtract(1, 'month'))} aria-label="이전 달" sx={{ border: '1px solid #E0E0E0' }}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#262626', minWidth: 150, textAlign: 'center' }}>
          {month.format('YYYY년 M월')}
        </Typography>
        <IconButton
          size="small" disabled={isCurrentMonth} onClick={() => setMonth(m => m.add(1, 'month'))}
          aria-label="다음 달" sx={{ border: '1px solid #E0E0E0' }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <Typography sx={{ fontSize: '12px', color: '#999', textAlign: 'center', mt: '-8px' }}>
        그 달에 끝난 수업의 예약 기준(취소·대기 제외){isCurrentMonth ? ' · 이번 달은 오늘까지' : ''}
      </Typography>

      {isError && <Typography sx={{ color: '#d32f2f', textAlign: 'center' }}>{errorMessages(error)}</Typography>}
      {isLoading && <Typography sx={{ color: '#888', textAlign: 'center' }}>불러오는 중...</Typography>}

      {/* 달을 바꾸면 key 가 바뀌어 애니메이션이 다시 돈다 */}
      {data && (
        <React.Fragment key={monthKey}>
          <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <SummaryCard label="총 진행 세션" color="#262626" value={summary.total} previous={previous?.total} good />
            {Object.entries(STATUS).map(([key, s]) => (
              <SummaryCard
                key={key} label={s.label} color={s.color} value={summary[key]} total={summary.total}
                previous={previous?.[key]} previousTotal={previous?.total} good={key === 'attended'}
              />
            ))}
          </Box>

          {Object.entries(STATUS).map(([key, s]) => (
            <Box key={key} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: '12px' }}>
              <Card title={`일별 ${s.label} 현황`}>
                <DailyLineChart
                  current={data.daily
                    .slice(0, isCurrentMonth ? dayjs().date() : data.daily.length)
                    .map(d => d.counts[key])}
                  previous={data.previousDaily.map(d => d.counts[key])}
                  color={s.color} currentLabel={currentLabel} previousLabel={previousLabel}
                />
              </Card>
              <Card title={`강사별 ${s.label} 현황`} center>
                <DonutChart
                  items={data.trainers
                    .map(t => ({ key: t.key, name: t.name, value: t.counts[key] }))
                    .filter(item => item.value > 0)
                    .sort((a, b) => b.value - a.value)}
                />
              </Card>
            </Box>
          ))}

          <TrainerClassTypeTable data={data} summary={summary} />
        </React.Fragment>
      )}
    </Box>
  );
}

export default ClassAttendanceStats;
